/**
 * Route Paths
 * Centralized route path definitions
 */

export const PATHS = {
    // Public
    HOME: '/',
    ABOUT: '/about',
    CONTACT: '/contact',
    // Kept for back-compat redirects: any external link / business
    // card / printed material still pointing at /business-card lands
    // on /contact via a <Navigate> in routes/index.tsx.
    BUSINESS_CARD: '/business-card',
    // Lead-capture form (Twilio OTP "reveal contact details" flow).
    // Lives separately so /contact can stay the polished card view.
    CONTACT_FORM: '/contact-form',
    // Public meeting room. The room_code maps to a row in `meetings`.
    // Same URL used by host and guests; the join token differentiates
    // permissions server-side.
    MEETING_ROOM: '/meet/:roomCode',
    PRIVACY: '/privacy-policy',
    TERMS: '/terms-of-service',

    // Services
    SERVICES: '/services',

    // Properties
    /** Legacy URL; redirects to REALESTATE. */
    PROPERTIES: '/properties',
    // Canonical browse path covering sale / rental / land / commercial.
    // The old /properties-for-sale URL still redirects here so bookmarks
    // and external links don't break.
    REALESTATE: '/realestate',
    // Kept for legacy redirects + tests; no new code should reference it.
    PROPERTIES_FOR_SALE: '/properties-for-sale',
    PROPERTY_DETAIL: '/property/:listing_id',

    // Open House
    OPEN_HOUSE: '/open-house',
    // Legacy placeholder form — redirects to OPEN_HOUSE in routes/index.tsx.
    OPEN_HOUSE_REGISTER: '/open-house/register',
    // Per-open-house visitor sign-in sheet (kiosk tablet at the door, or
    // the visitor's own phone via the printed QR). `:code` is
    // open_houses.public_code. Chrome-free — mounted outside MainLayout
    // like /meet/:roomCode.
    OPEN_HOUSE_SIGN_IN: '/open-house/:code/sign-in',
    // A visiting agent's phone, scanned from the kiosk QR: upload the
    // business-card photo against their per-visitor token.
    OPEN_HOUSE_BUSINESS_CARD: '/open-house/:code/card/:token',

    // Articles the owner publishes from the social editor (/manage/posts).
    // Each one is also the canonical page the X / Facebook / Threads posts
    // link back to, so the slug never changes after publishing.
    POSTS: '/posts',
    POST_DETAIL: '/posts/:slug',

    // Agreements
    OUR_AGREEMENTS: '/agreements',
    AGREEMENT_SIGNUP: '/agreements/get-started',
    BUYERS_AGREEMENT_REVIEW: '/buyers-agreement/review',
    AGREEMENT_APPROVE: '/agreement/approve/:token',

    // Tools
    MORTGAGE_CALCULATOR: '/mortgage-calculator',
    // "What's my home worth?" — a seller's guided valuation; the texted /
    // emailed link confirms him and opens his report.
    HOME_VALUE: '/home-value',
    HOME_VALUE_CONFIRM: '/home-value/confirm/:token',
    HOME_VALUE_REPORT: '/home-value/report/:token',
    SAVED_SEARCH: '/saved-search/:token',
    // Email-link landing for the chat 2FA flow used by international
    // visitors (since Twilio SMS is gated to US/Canada). The link
    // emailed by Vero lands here, the page POSTs the token to the
    // backend, and the chat tab — still open — polls /session-status
    // and resumes automatically once it sees email_verified = true.
    CHAT_VERIFY_EMAIL: '/chat/verify-email',

    // Public, customer-facing lender profile. Returns a clean
    // "Not found" view when the operator's customer_page_enabled
    // promo toggle is off.
    LENDER_PUBLIC: '/lender/:contactId',

    // QR cross-device login — the PHONE lands here after scanning
    // the code shown on the desktop login page. Public route; the
    // approve action itself requires signing in on the phone.
    QR_APPROVE: '/qr-approve',

    // Public tokenized Me-Card view — renders only the fields the
    // operator selected when minting this share URL.
    SHARE_CARD: '/card/:token',

    // Sponsor self-serve portal — paid lender placements on
    // realestatewithshayaan.com. Login is by magic email link
    // (no password). All three sponsor pages live behind that
    // token-based auth.
    // Lenders apply here; the operator approves on /manage/sponsors and the
    // approval email links to SPONSOR_SUBSCRIBE?token=… (single-use).
    SPONSOR_APPLY: '/sponsor/apply',
    SPONSOR_SUBSCRIBE: '/sponsor/subscribe',
    SPONSOR_LOGIN: '/sponsor/login',
    SPONSOR_PORTAL: '/sponsor/portal',
    SPONSOR_CHECKOUT_SUCCESS: '/sponsor/checkout/success',
    SPONSOR_CHECKOUT_CANCEL: '/sponsor/checkout/cancel',

    // Auth
    AUTH: {
        LOGIN: '/auth/signin',
        SIGNUP: '/auth/signup',
        VERIFY_EMAIL: '/verify-email',
    },

    // User
    PROFILE: '/profile',
    BOOK_APPOINTMENT: '/book-appointment',

    // Admin
    ADMIN: {
        ROOT: '/manage',
        LOGIN: '/manage/login',
        REGISTER: '/manage/register', // Deprecated: redirects to AUTH.SIGNUP
        DASHBOARD: '/manage/dashboard',
        CONTACTS: '/manage/contacts',
        CONTACT_ADD: '/manage/contacts/new',
        CONTACT_DETAIL: '/manage/contacts/:contactId',
        CONTACT_EDIT: '/manage/contacts/:contactId/edit',
        COMPANIES: '/manage/companies',
        COMPANY_DETAIL: '/manage/companies/:contactId',
        MEETINGS: '/manage/meetings',
        ROOMS: '/manage/rooms',
        ROOM_DETAIL: '/manage/rooms/:slug',
        PROPERTIES: '/manage/properties',
        PROPERTY_ADD: '/manage/properties/add',
        PROPERTY_SCRAPE: '/manage/properties/scrape-zillow',
        PROPERTY_DETAIL: '/manage/properties/:propertyId',
        // Open houses — the list is the "Open Houses" tab on PROPERTIES
        // (?tab=openHouses); create + detail get their own pages because
        // the scraped preview / QR / sign-in table need the room.
        OPEN_HOUSE_NEW: '/manage/open-houses/new',
        OPEN_HOUSE_DETAIL: '/manage/open-houses/:openHouseId',
        OPEN_HOUSE_PRINT: '/manage/open-houses/:openHouseId/print',
        AGREEMENTS: '/manage/agreements',
        AFFILIATES: '/manage/affiliates',
        COMMUNICATION: '/manage/communication-center',
        // Numbers the assistant never auto-replies to (lead-gen services,
        // dialers). Separate from banned numbers, which block abusive
        // senders entirely.
        AUTO_REPLY_EXCLUSIONS: '/manage/auto-reply-exclusions',
        CALL_HISTORY: '/manage/call-history',
        SETTINGS: '/manage/settings',
        SECURITY: '/manage/security',
        MAIL: '/manage/mail',
        BIN: '/manage/bin',
        AUDIT_LOGS: '/manage/audit-logs',
        LICENSE_CHECK: '/manage/license-check',
        AI_SETTINGS: '/manage/ai-settings',
        LIVEKIT_SETTINGS: '/manage/livekit-settings',
        EMAIL_BLACKLIST: '/manage/email-blacklist',
        SCRAPER_SETTINGS: '/manage/scraper-settings',
        // Home valuations: sellers' requests, the recorded sales behind
        // them, and where those sales come from.
        HOME_VALUES: '/manage/home-values',
        RECENT_SALES: '/manage/recent-sales',
        VALUATION_SOURCES: '/manage/valuation-sources',
        INTEGRATIONS: '/manage/integrations',
        BRANDS: '/manage/brands',
        SETUP: '/manage/setup',
        COMPLIANCE: '/manage/compliance',
        // Sponsor billing — paid lender placements in the AI-chat
        // referral cards. Tabs: subscriptions list, plan CRUD,
        // billing-failures view, Stripe settings.
        SPONSORS: '/manage/sponsors',
        ME_CARD: '/manage/me-card',
        // Admin view of all AI chat sessions with full transcripts —
        // surfaces leads as they come in, including any safety alerts
        // / threats the visitor sent so the operator can review them.
        AI_CHAT_SESSIONS: '/manage/ai-chat-sessions',
        // Archive & Download — lists server-side recordings + PDFs so
        // the operator can download them locally and free up storage.
        ARCHIVE: '/manage/archive',
        // Playwright / pytest run history + live stream. Shows past
        // runs with status badges and a "Run now" button that POSTs
        // to /api/test-runs/dispatch; detail view subscribes to the
        // /api/test-runs/{id}/stream WebSocket for live updates.
        TEST_RUNS: '/manage/test-runs',
        TEST_RUN_DETAIL: '/manage/test-runs/:runId',
        // Visitor-submitted bug reports from Vero's "Report a
        // problem" menu. Lists submissions with status filter,
        // detail drawer with screenshot + console + page state.
        PROBLEM_REPORTS: '/manage/problem-reports',
        // Bug-tracker view of incidents (aggregates many problem
        // reports for the same URL into one ticket — drives
        // priority, assignment, resolution, and the impacted-user
        // email blast).
        INCIDENTS: '/manage/incidents',
        INCIDENT_DETAIL: '/manage/incidents/:incidentId',
        // Social publishing — one post written with AI, one variant per
        // platform, tracked links back to the site. The engagement page
        // is a sibling of the editor, so it is declared before it.
        POSTS: '/manage/posts',
        POST_NEW: '/manage/posts/new',
        SOCIAL_ANALYTICS: '/manage/posts/analytics',
        POST_EDIT: '/manage/posts/:postId',
    },
} as const;

// Helper to build dynamic paths
export const buildPath = {
    propertyDetail: (id: string | number) => `/property/${id}`,
    postDetail: (slug: string) => `/posts/${encodeURIComponent(slug)}`,
    contactDetail: (id: string | number) => `/manage/contacts/${id}`,
    contactEdit: (id: string | number) => `/manage/contacts/${id}/edit`,
    contactMessages: (id: string | number) => `/manage/communication-center?contactId=${id}`,
    companyDetail: (id: string | number) => `/manage/companies/${id}`,
    propertyAdminDetail: (id: string | number) => `/manage/properties/${id}`,
    agreementApprove: (token: string) => `/agreement/approve/${token}`,
    savedSearch: (token: string) => `/saved-search/${token}`,
    homeValueReport: (token: string) => `/home-value/report/${token}`,
    openHouseSignIn: (code: string) => `/open-house/${code}/sign-in`,
    openHouseBusinessCard: (code: string, token: string) => `/open-house/${code}/card/${token}`,
    openHouseAdminDetail: (id: string | number) => `/manage/open-houses/${id}`,
    openHouseAdminPrint: (id: string | number) => `/manage/open-houses/${id}/print`,
    postEdit: (id: string | number) => `/manage/posts/${id}`,
};

export default PATHS;
